import { useEffect, useMemo, useState } from 'react';
import { Stack, Typography } from '@mui/material';
import {
  CustomerSummaryModal,
  type CustomerSummaryColumn,
  type CustomerSummarySectionAny,
} from '@/components/iem/shared/CustomerSummaryModal';
import type {
  IssueAdditionalCartItem,
  IssueBuyProgramCartItem,
  IssuePlannerItem,
  IssuePlannerCartItem,
} from './types';
import type { SizeAllocationPlaceholder } from './IssueCartSidebar';
import { calculateBuyProgramTotal, calculateLineTotal, formatCurrency } from './buyProgramPricing';

export interface IssueCustomerSummaryModalProps {
  open: boolean;
  onClose: () => void;
  unitName?: string;
  memberName?: string;
  ieCartItems: IssuePlannerCartItem[];
  iePlannerItems?: IssuePlannerItem[];
  sizeAllocationPlaceholders: SizeAllocationPlaceholder[];
  additionalItems: IssueAdditionalCartItem[];
  buyProgramItems: IssueBuyProgramCartItem[];
}

type IssueSummaryPhrRow = {
  key: string;
  name: string;
  lin?: string | null;
  kitName?: string | null;
  sizeLabel?: string | null;
  authorizedQty?: number | null;
  quantity: number;
  status?: string;
};

type IssueSummaryThrRow = {
  key: string;
  name: string;
  lin?: string | null;
  sizeLabel?: string | null;
  ieItem?: string | null;
  authorizedQty?: number | null;
  quantity: number;
};

type IssueSummaryBuyProgramRow = {
  key: string;
  name: string;
  quantity: number;
  actualPrice: number;
  tax: number;
  shipping: number;
  lineTotal: number;
};

export const IssueCustomerSummaryModal = ({
  open,
  onClose,
  unitName,
  memberName,
  ieCartItems,
  iePlannerItems = [],
  sizeAllocationPlaceholders,
  additionalItems,
  buyProgramItems,
}: IssueCustomerSummaryModalProps) => {
  const [generatedAt, setGeneratedAt] = useState<Date>(() => new Date());

  useEffect(() => {
    if (open) {
      setGeneratedAt(new Date());
    }
  }, [open]);

  const iePlannerById = useMemo(() => {
    const map = new Map<string, IssuePlannerItem>();
    iePlannerItems.forEach((item) => {
      map.set(item.id, item);
    });
    return map;
  }, [iePlannerItems]);

  const iePlannerByCatalogId = useMemo(() => {
    const map = new Map<
      string,
      { lin: string | null; authorizedQty: number }
    >();
    iePlannerItems.forEach((item) => {
      const catalogId = item.catalogId?.trim();
      if (!catalogId) {
        return;
      }

      const existing = map.get(catalogId);
      if (existing) {
        map.set(catalogId, {
          lin: existing.lin ?? item.lin ?? null,
          authorizedQty: existing.authorizedQty + (item.authorizedQty ?? 0),
        });
        return;
      }

      map.set(catalogId, {
        lin: item.lin ?? null,
        authorizedQty: item.authorizedQty ?? 0,
      });
    });
    return map;
  }, [iePlannerItems]);

  const iePlannerByKitAndLin = useMemo(() => {
    const map = new Map<string, { authorizedQty: number }>();
    iePlannerItems.forEach((item) => {
      const normalizedLin = item.lin?.trim().toUpperCase();
      if (!normalizedLin || normalizedLin === '—') {
        return;
      }
      const ieKitId = item.ieKitId?.trim() ?? '';
      const key = `${ieKitId}::${normalizedLin}`;
      const existing = map.get(key);
      if (existing) {
        map.set(key, {
          authorizedQty: existing.authorizedQty + (item.authorizedQty ?? 0),
        });
        return;
      }
      map.set(key, {
        authorizedQty: item.authorizedQty ?? 0,
      });
    });
    return map;
  }, [iePlannerItems]);

  const phrRows = useMemo<IssueSummaryPhrRow[]>(() => {
    const placeholderRows: IssueSummaryPhrRow[] = sizeAllocationPlaceholders.map((placeholder) => {
      const plannerMeta = iePlannerByCatalogId.get(placeholder.parentCatalogId);
      return {
        key: `placeholder:${placeholder.id}`,
        name: placeholder.title,
        lin: plannerMeta?.lin ?? null,
        kitName: null,
        sizeLabel: placeholder.subtitle ?? null,
        authorizedQty: plannerMeta?.authorizedQty ?? null,
        quantity: placeholder.quantity,
        status: 'Awaiting size selection',
      };
    });

    const ieRows: IssueSummaryPhrRow[] = ieCartItems.map((item) => {
      const plannerById = iePlannerById.get(item.plannerItemId);
      const normalizedLin = item.lin?.trim().toUpperCase();
      const ieKitId = item.ieKitId?.trim() ?? '';
      const plannerByKitAndLin =
        normalizedLin && normalizedLin !== '—'
          ? (iePlannerByKitAndLin.get(`${ieKitId}::${normalizedLin}`) ??
            iePlannerByKitAndLin.get(`::${normalizedLin}`))
          : undefined;
      const plannerByCatalog = item.selectedCatalogId
        ? iePlannerByCatalogId.get(item.selectedCatalogId)
        : undefined;

      return {
        key: `ieItem:${item.id}`,
        name: item.name,
        lin: item.lin || plannerById?.lin || plannerByCatalog?.lin || null,
        kitName: item.kitName ?? null,
        sizeLabel: item.sizeLabel ?? null,
        authorizedQty:
          plannerById?.authorizedQty ??
          plannerByKitAndLin?.authorizedQty ??
          plannerByCatalog?.authorizedQty ??
          null,
        quantity: item.assetIds.length,
      };
    });

    return [...placeholderRows, ...ieRows].filter((row) => row.quantity > 0);
  }, [
    ieCartItems,
    iePlannerByCatalogId,
    iePlannerById,
    iePlannerByKitAndLin,
    sizeAllocationPlaceholders,
  ]);

  const thrRows = useMemo<IssueSummaryThrRow[]>(() => {
    return additionalItems
      .map((item) => ({
        key: `thr:${item.groupId}`,
        name: item.name,
        lin: item.lin ?? null,
        sizeLabel: item.sizeLabel ?? null,
        ieItem: null,
        authorizedQty: null,
        quantity: item.assetIds.length,
      }))
      .filter((row) => row.quantity > 0);
  }, [additionalItems]);

  const buyProgramRows = useMemo<IssueSummaryBuyProgramRow[]>(() => {
    return buyProgramItems
      .map((item) => ({
        key: `buy:${item.id}`,
        name: item.catalog.name,
        quantity: item.quantity,
        actualPrice: item.actualPrice,
        tax: item.tax,
        shipping: item.shipping,
        lineTotal: calculateLineTotal(item),
      }))
      .filter((row) => row.quantity > 0);
  }, [buyProgramItems]);

  const totals = useMemo(() => {
    const phrQty = phrRows.reduce((sum, row) => sum + row.quantity, 0);
    const thrQty = thrRows.reduce((sum, row) => sum + row.quantity, 0);
    const buyQty = buyProgramRows.reduce((sum, row) => sum + row.quantity, 0);
    const buyTotal = calculateBuyProgramTotal(buyProgramItems);
    return { phrQty, thrQty, buyQty, buyTotal, overallQty: phrQty + thrQty + buyQty };
  }, [buyProgramItems, buyProgramRows, phrRows, thrRows]);

  const sections = useMemo(() => {
    const phrColumns: CustomerSummaryColumn<IssueSummaryPhrRow>[] = [
      {
        header: 'LIN',
        width: '15%',
        render: (row) => <Typography variant="body2">{row.lin ?? '—'}</Typography>,
        printValue: (row) => row.lin ?? '—',
      },
      {
        header: 'Nomenclature',
        width: '30%',
        render: (row) => (
          <Stack spacing={0.25}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {row.name}
            </Typography>
            {row.status ? (
              <Typography variant="caption" color="text.secondary">
                {row.status}
              </Typography>
            ) : null}
          </Stack>
        ),
        printValue: (row) => {
          return { primary: row.name, secondary: row.status || undefined };
        },
      },
      {
        header: 'Size',
        width: '15%',
        render: (row) => <Typography variant="body2">{row.sizeLabel ?? '—'}</Typography>,
        printValue: (row) => row.sizeLabel ?? '—',
      },
      {
        header: 'Authorized Qty',
        width: '15%',
        align: 'right',
        render: (row) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {typeof row.authorizedQty === 'number' ? row.authorizedQty : '—'}
          </Typography>
        ),
        printValue: (row) => (typeof row.authorizedQty === 'number' ? row.authorizedQty : '—'),
      },
      {
        header: 'Qty',
        width: '10%',
        align: 'right',
        render: (row) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.quantity}
          </Typography>
        ),
        printValue: (row) => row.quantity,
      },
    ];

    const thrColumns: CustomerSummaryColumn<IssueSummaryThrRow>[] = [
      {
        header: 'LIN',
        width: '15%',
        render: (row) => <Typography variant="body2">{row.lin ?? '—'}</Typography>,
        printValue: (row) => row.lin ?? '—',
      },
      {
        header: 'Nomenclature',
        width: '30%',
        render: (row) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.name}
          </Typography>
        ),
        printValue: (row) => ({ primary: row.name }),
      },
      {
        header: 'Size',
        width: '15%',
        render: (row) => <Typography variant="body2">{row.sizeLabel ?? '—'}</Typography>,
        printValue: (row) => row.sizeLabel ?? '—',
      },
      {
        header: 'Authorized Qty',
        width: '15%',
        align: 'right',
        render: (row) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {typeof row.authorizedQty === 'number' ? row.authorizedQty : '—'}
          </Typography>
        ),
        printValue: (row) => (typeof row.authorizedQty === 'number' ? row.authorizedQty : '—'),
      },
      {
        header: 'Qty',
        width: '10%',
        align: 'right',
        render: (row) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.quantity}
          </Typography>
        ),
        printValue: (row) => row.quantity,
      },
    ];

    const buyProgramColumns: CustomerSummaryColumn<IssueSummaryBuyProgramRow>[] = [
      {
        header: 'Item',
        render: (row) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.name}
          </Typography>
        ),
        printValue: (row) => ({ primary: row.name }),
      },
      {
        header: 'Qty',
        align: 'right',
        render: (row) => (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.quantity}
          </Typography>
        ),
        printValue: (row) => row.quantity,
      },
      {
        header: 'Price/ea',
        align: 'right',
        render: (row) => <Typography variant="body2">{formatCurrency(row.actualPrice)}</Typography>,
        printValue: (row) => formatCurrency(row.actualPrice),
      },
      {
        header: 'Tax/ea',
        align: 'right',
        render: (row) => (
          <Typography variant="body2">{row.tax > 0 ? formatCurrency(row.tax) : '—'}</Typography>
        ),
        printValue: (row) => (row.tax > 0 ? formatCurrency(row.tax) : '—'),
      },
      {
        header: 'Shipping/ea',
        align: 'right',
        render: (row) => (
          <Typography variant="body2">
            {row.shipping > 0 ? formatCurrency(row.shipping) : '—'}
          </Typography>
        ),
        printValue: (row) => (row.shipping > 0 ? formatCurrency(row.shipping) : '—'),
      },
      {
        header: 'Line Total',
        align: 'right',
        render: (row) => (
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {formatCurrency(row.lineTotal)}
          </Typography>
        ),
        printValue: (row) => formatCurrency(row.lineTotal),
      },
    ];

    const summary: CustomerSummarySectionAny[] = [
      {
        key: 'phr',
        title: 'PHR (IE)',
        rows: phrRows,
        columns: phrColumns,
        getRowKey: (row: IssueSummaryPhrRow) => row.key,
      },
      {
        key: 'thr',
        title: 'THR (Additional Items)',
        rows: thrRows,
        columns: thrColumns,
        getRowKey: (row: IssueSummaryThrRow) => row.key,
      },
      {
        key: 'buy-program',
        title: 'Buy Program',
        rows: buyProgramRows,
        columns: buyProgramColumns,
        getRowKey: (row: IssueSummaryBuyProgramRow) => row.key,
        printSummary:
          totals.buyTotal > 0
            ? { label: 'Reimbursement Total', value: formatCurrency(totals.buyTotal) }
            : undefined,
      },
    ];

    return summary;
  }, [buyProgramRows, phrRows, thrRows, totals.buyTotal]);

  return (
    <CustomerSummaryModal
      open={open}
      onClose={onClose}
      title="Issue Summary"
      workflowLabel="Issue"
      unitName={unitName}
      memberName={memberName}
      sections={sections}
      generatedAt={generatedAt}
      summaryRight={
        <>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Total Qty: {totals.overallQty}
          </Typography>
          {totals.buyTotal > 0 ? (
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
              Reimbursement Total: {formatCurrency(totals.buyTotal)}
            </Typography>
          ) : null}
        </>
      }
    />
  );
};
